import { type ReactNode } from 'react';
import {
  RadioGroup as MuiRadioGroup,
  FormControl,
  FormLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import LabeledRadio from 'components/LabeledRadio/LabeledRadio';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';

import { type FormInputProps } from './types';
import { useFormInputChange } from './useFormInputChange';

const useStyles = makeStyles({
  groupLabel: {
    ...extendedFormsStyle.labelHorizontal,
    marginBottom: '-8px',
  },
  radioGroup: {
    '& .MuiFormControlLabel-root': {
      marginTop: '-2px',
      marginBottom: '-2px',
    },
  },
});

type RadioOption<TValue extends string> = {
  readonly value: TValue;
  readonly label: ReactNode;
};

type RadioGroupProps<TValue extends string> = FormInputProps<TValue> & {
  readonly options: readonly RadioOption<TValue>[];
};

const RadioGroup = <TValue extends string>({
  value,
  label,
  className,
  disabled,
  options,
  ...props
}: RadioGroupProps<TValue>) => {
  const classes = useStyles();
  const name = 'name' in props ? props.name : undefined;
  const emit = useFormInputChange(props);

  const handleRadioChange = (
    _event: React.ChangeEvent<{ [key: string]: unknown }>,
    selectedValue: string
  ) => {
    emit(selectedValue as TValue);
  };

  return (
    <FormControl component="fieldset" className={className} disabled={disabled}>
      {label && (
        <FormLabel
          component="legend"
          className={classes.groupLabel}
          focused={false}
        >
          {label}
        </FormLabel>
      )}
      <MuiRadioGroup
        row={false}
        name={name}
        value={value}
        onChange={handleRadioChange}
        className={classes.radioGroup}
      >
        {options.map(option => (
          <LabeledRadio
            key={option.value}
            label={option.label}
            value={option.value}
            disabled={disabled}
          />
        ))}
      </MuiRadioGroup>
    </FormControl>
  );
};

export default RadioGroup;
